import React from "react";
import clsx from "clsx";

import { ReactComponent as IconUser } from "../../../assets/images/AnswerList/icon_user.svg";
import { ReactComponent as IconPencil } from "../../../assets/images/AnswerList/icon_pencil.svg";
import { ReactComponent as IconEyeOn } from "../../../assets/images/AnswerList/icon_eye_on.svg";
import { ReactComponent as IconEyeOff } from "../../../assets/images/AnswerList/icon_eye_off.svg";
import { ReactComponent as IconTrash } from "../../../assets/images/AnswerList/icon_trash.svg";
import { IAnswerComment, ITeacherR } from "../../../state";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { generateFullName } from "../../../libs/Util";
import { getConfirmModalMessage } from "../../../libs/domUtils";

import styles from "./FormReply.module.scss";

export type FormReplyProps = {
  comment: IAnswerComment;
  answerId: number;
  name_opened: boolean;
  calling?: boolean;
  currentTeacherID: number;
  className?: string;
  iconWrapperClassName?: string;
  disableCancelConfirm?: boolean;
  handleDeleteComment: (answer_id: number, comment_id: number) => void;
  handleComment: (
    content: string,
    answerID: number,
    comment_id?: number,
  ) => void;
  handleToggleComment: (
    answer_id: number,
    comment_id: number,
    is_hidden: boolean,
  ) => void;
};

const FormReply: React.FC<FormReplyProps> = ({
  comment,
  answerId,
  name_opened,
  calling,
  currentTeacherID,
  className,
  iconWrapperClassName,
  disableCancelConfirm,
  handleComment,
  handleToggleComment,
  handleDeleteComment,
}) => {
  const [showForm, setShowForm] = React.useState(false);
  const [content, setContent] = React.useState(comment.content);
  const [openModal, setOpenModal] = React.useState<{
    state: boolean;
    type: "delete" | "hide" | "edit";
    commentId: number;
    hiddenBy?: null | ITeacherR;
  }>({
    state: false,
    type: "delete",
    commentId: 0,
  });

  const updateComment = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleComment(content, answerId, comment.id);
    setShowForm(false);
  };

  const isStudent = comment.student_id || comment.student;

  const modalMessage = getConfirmModalMessage({
    type: openModal.type,
    hiddenBy: openModal.hiddenBy,
  });

  const handleModalConfirm = () => {
    if (openModal.type === "delete") {
      handleDeleteComment(answerId, openModal.commentId);
    }

    if (openModal.type === "edit") {
      setShowForm(false);
      setContent(comment.content);
    }

    if (openModal.type === "hide") {
      handleToggleComment(answerId, openModal.commentId, !openModal.hiddenBy);
    }

    setOpenModal({
      state: false,
      type: "delete",
      commentId: 0,
    });
  };

  const isMyComment =
    comment.teacher?.id === currentTeacherID ||
    comment.teacher_id === currentTeacherID;

  return (
    <div className={clsx(styles.body, className)} key={comment.id}>
      {iconWrapperClassName ? (
        <span className={iconWrapperClassName}>
          <IconUser
            className={clsx(styles.userIcon, !isStudent && styles.isTeacher)}
          />
        </span>
      ) : (
        <IconUser
          className={clsx(styles.userIcon, !isStudent && styles.isTeacher)}
        />
      )}
      <div className={styles.wrapper}>
        <div className={styles.header}>
          {isStudent ? (
            name_opened ? (
              <p className={clsx(styles.name, styles.student)}>
                {comment.student_id
                  ? comment.name
                  : generateFullName([
                      comment.student?.last_name,
                      comment.student?.first_name,
                    ])}
                {comment.hidden_by !== null && " (生徒に非表示になっています)"}
              </p>
            ) : (
              <p className={clsx(styles.name, styles.student)}>
                (生徒に非表示になっています)
              </p>
            )
          ) : (
            <p className={clsx(styles.name, styles.teacher)}>
              {comment.teacher_id
                ? comment.name
                : generateFullName([
                    comment.teacher?.last_name,
                    comment.teacher?.first_name,
                  ])}{" "}
              {isMyComment && "（あなたのコメント）"}
              {comment.hidden_by !== null && " (生徒に非表示になっています)"}
            </p>
          )}
        </div>
        <div className={styles.content}>
          {showForm ? (
            <form onSubmit={updateComment} className={styles.form}>
              <textarea
                className={styles.textarea}
                rows={3}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="コメントを入力してください。"
              />
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => {
                    if (!disableCancelConfirm && content !== comment.content) {
                      setOpenModal({
                        state: true,
                        type: "edit",
                        commentId: comment.id,
                      });
                    } else {
                      setShowForm(false);
                      setContent(comment.content);
                    }
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className={styles.saveButton}
                  disabled={calling || !content || content === comment.content}
                >
                  保存する
                </button>
              </div>
            </form>
          ) : (
            <div className={styles.comment}>
              <p className={styles.text}>{comment.content}</p>
              <div className={styles.actions}>
                {(isMyComment || comment.student) && (
                  <>
                    {!isStudent && (
                      <IconPencil
                        className={clsx(calling && styles.disabled)}
                        onClick={() => {
                          setShowForm(true);
                        }}
                      />
                    )}
                    {comment.hidden_by === null ? (
                      <IconEyeOn
                        className={clsx(calling && styles.disabled)}
                        onClick={() =>
                          setOpenModal({
                            state: true,
                            type: "hide",
                            commentId: comment.id,
                            hiddenBy: comment.hidden_by,
                          })
                        }
                      />
                    ) : (
                      <IconEyeOff
                        className={clsx(calling && styles.disabled)}
                        onClick={() =>
                          setOpenModal({
                            state: true,
                            type: "hide",
                            commentId: comment.id,
                            hiddenBy: comment.hidden_by,
                          })
                        }
                      />
                    )}
                    <IconTrash
                      className={clsx(calling && styles.disabled)}
                      onClick={() => {
                        setOpenModal({
                          state: true,
                          type: "delete",
                          commentId: comment.id,
                        });
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {openModal.state && (
        <ModalDeleteConfirm
          title={modalMessage}
          onConfirm={handleModalConfirm}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              commentId: 0,
            })
          }
        />
      )}
    </div>
  );
};

export default FormReply;
